import { Workbook } from "exceljs";
import * as ExcelJS from "exceljs";
import { useRecoilValue } from "recoil";
import { pubState } from "../../../recoil/pub.atom";
import { Const } from "../../../common/const/const";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { PaymentMethod, ExpenseSubType } from "../../../api/settlement";
import { getSettlementDaily } from "../../../api/settlement-daily";
ChartJS.register(ArcElement, Tooltip, Legend);

export const UseDailyExcel = () => {
  const pub = useRecoilValue(pubState);

  const backgroundBlue = new Set([
    "현금",
    "계좌",
    "카드",
    "매장포인트",
    "래빗",
    "무료바인",
    "디파짓",
    "합계",
    "시상",
    "수익",
    "기타",
    "부상",
    "입금내역",
    "게임매출",
    "기타매출",
    "게임매출(현금/계좌/카드)",
    "출금내역",
    "인건비 ",
    "딜러",
    "카운터",
    "플로어",
    "합계",
    "디파짓 수익",
    "전일 잔액합",
    "순수익",
    "장부 잔액합",
    "정산 잔액합",
    "당일카드입금",
    "당일카드출금",
    "실 잔고",
    " ",
  ]);

  const backgroundPink = new Set([
    "포인트 / 무료",
    "전체매출(포인트/무료 포함)",
  ]);

  const createDailyFile = async (date: string) => {
    if (!pub) return;
    const data = await getSettlementDaily(pub?.id, date);
    const revenue = data.revenue;
    const expense = data.expense;
    const laborExpense = data.laborExpense;
    const tournamentList = data.tournaments;
    const seedInfo = data.seedInfo;

    if (!data) return;

    const workbook = new ExcelJS.Workbook();
    workbook.calcProperties = { fullCalcOnLoad: true };
    const sheet = workbook.addWorksheet("러너정산");

    sheet.mergeCells("A1 : I1");
    sheet.getCell("A1").value = `${pub?.pubName} 게임별 상세 내역 ${date}`;

    sheet.mergeCells("L1 : Q1");
    sheet.getCell("L1").value = `${date} ${pub?.pubName} 입출금 내역`;

    // 1열 폰트 사이즈 지정
    sheet.getRow(1).font = { size: 20, bold: true };

    // 토너먼트 관련 값 매핑
    tournamentList.forEach((t, i) => {
      const base = 3 + i * 10;
      const row1 = base;
      const row2 = base + 1;
      const row3 = base + 2;
      const row4 = base + 3;
      const row5 = base + 5;
      const row6 = base + 6;
      const row7 = base + 7;

      sheet.mergeCells(`A${row1 - 1} : A${row1}`);
      sheet.getCell(`A${row1}`).value = t.title;
      sheet.getCell(`A${row1}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFCC" },
      };

      sheet.mergeCells(`A${row2} : A${row4}`);
      sheet.getCell(`A${row2}`).value = t.buyIn
        ? `${t.buyIn / 10000}만 바인`
        : "-";
      sheet.getCell(`A${row2}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFCC" },
      };

      ["B", "C", "D", "E", "F", "G", "H", "I"].forEach((col, idx) => {
        const val = [
          "현금",
          "계좌",
          "카드",
          "매장포인트",
          "래빗",
          "무료바인",
          "디파짓",
          "합계",
        ][idx];
        sheet.getCell(`${col}${row1}`).value = val;
      });

      sheet.getCell(`B${row2}`).value = t.revenue.cashRevenue;
      sheet.getCell(`C${row2}`).value = t.revenue.bankRevenue;
      sheet.getCell(`D${row2}`).value = t.revenue.cardRevenue;
      sheet.getCell(`E${row2}`).value = (
        t.revenue.pointRevenue / Const.POINT_RATE
      )?.toLocaleString();
      sheet.getCell(`F${row2}`).value = (
        t.revenue.seedRevenue / Const.POINT_RATE
      )?.toLocaleString();
      sheet.getCell(`G${row2}`).value = t.revenue.discountAmount;
      sheet.getCell(`H${row2}`).value = t.revenue.depositRevenue;
      sheet.getCell(`I${row2}`).value = "앤트리";

      sheet.getCell(`I${row3}`).value = t.entryCount?.toLocaleString();

      sheet.getCell(`B${row4}`).value = t.revenue.cashRevenue;
      sheet.getCell(`C${row4}`).value = t.revenue.bankRevenue;
      sheet.getCell(`D${row4}`).value = t.revenue.cardRevenue;
      sheet.getCell(`E${row4}`).value = t.revenue.pointRevenue; // 현금화 된 포인트
      sheet.getCell(`F${row4}`).value = t.revenue.seedRevenue; // 현금화 된 크레딧
      sheet.getCell(`G${row4}`).value = t.revenue.discountAmount;
      sheet.getCell(`H${row4}`).value = t.revenue.depositRevenue; // 디파짓
      sheet.getCell(`I${row4}`).value = {
        formula: `=SUM(B${row4}:H${row4})`,
      }; // 결제 총 합

      sheet.getCell(`A${row5}`).value = "시상";
      sheet.getCell(`G${row5}`).value = "수익";

      sheet.getCell(`A${row6}`).value = "기타";
      sheet.getCell(`B${row6}`).value = "래빗";
      sheet.getCell(`C${row6}`).value = "매장포인트";
      sheet.getCell(`D${row6}`).value = "부상";
      sheet.getCell(`G${row6}`).value = "현금";
      sheet.getCell(`H${row6}`).value = "계좌";
      sheet.getCell(`I${row6}`).value = "카드";

      // 시상
      sheet.getCell(`A${row7}`).value = t.prize.otherPrize; // 기타 시상
      sheet.getCell(`B${row7}`).value = t.prize.seedPrize; // 래빗 시상 (현금화)
      sheet.getCell(`C${row7}`).value = t.prize.pointPrize; // 매장이용권
      sheet.getCell(`D${row7}`).value = t.prize.subPrize; // 부상
      sheet.getCell(`E${row7}`).value = {
        formula: `=SUM(A${row7}:D${row7})`,
      }; // 시상 총 합

      // 시상
      sheet.getCell(`E${row7}`).value = t.prize.totalPrize;
      sheet.getCell(`G${row7}`).value = {
        formula: `=B${row2}-A${row7}`,
      }; // 현금 (현금 총 합 - 기타 시상)
      sheet.getCell(`H${row7}`).value = t.revenue.bankRevenue; // 계좌
      sheet.getCell(`I${row7}`).value = t.revenue.cardRevenue; // 카드
    });

    // 입출금 내역 값 매핑 ============================================================
    // 현금 계좌 카드 합계는 나란히 있음
    const lastCell = 13 + expense.length;
    const baseCells = [
      "M3",
      "M13",
      `L${lastCell + 4}`, // 디파짓 수익
      `L${lastCell + 8}`, // 전일 잔액합
      `L${lastCell + 12}`, // 순수익
      `L${lastCell + 16}`, // 장부 잔액합
      `L${lastCell + 20}`, // 장신 진엑 힙
      `L${lastCell + 24}`, // 실잔고
    ];
    const values = ["현금", "계좌", "카드", "합계"];
    baseCells.forEach((baseCell) => {
      const { col, row } = sheet.getCell(baseCell);

      values.forEach((val, idx) => {
        const currentCol = col + idx;

        if (+row === 13 && +currentCol === 16) return;

        sheet.getCell(row, currentCol).value = val;
      });
    });

    // 입금내역 =========================================================================
    sheet.getCell(`L3`).value = "입금내역";
    sheet.getCell(`P3`).value = "포인트 / 무료";
    sheet.getCell(`Q3`).value = "래빗";
    sheet.getCell(`L4`).value = "게임매출";
    sheet.getCell(`L5`).value = "기타매출";

    // 각 토너먼트 결제방식 값 위치 계산
    const blockRow4 = tournamentList.map((_, i) => 3 + i * 10 + 3);
    // 입금내역 셀에 결제 매소드 매핑
    const tournamentRevenueCell = [
      { cell: "M", formula: "B", method: PaymentMethod.CASH },
      { cell: "N", formula: "C", method: PaymentMethod.BANK_TRANSFER },
      { cell: "O", formula: "D", method: PaymentMethod.CARD },
      {
        cell: "P",
        formula: "E",
        subFormula: "G",
        method: PaymentMethod.POINTS,
      },
      { cell: "Q", formula: "F", method: PaymentMethod.SEED },
    ];

    tournamentRevenueCell.forEach((c) => {
      // 기타매출
      const other = revenue?.otherRevenue.find(
        (t) => t.paymentMethod === c.method
      );
      sheet.getCell(`${c.cell}5`).value = other?.amount;

      // 포인트 / 무료
      if (c.method === PaymentMethod.POINTS) {
        const pointFormula = `=SUM(${blockRow4
          .map((r) => `${c.formula}${r}`)
          .join(",")}) + SUM(${blockRow4
          .map((r) => `${c.subFormula}${r}`)
          .join(",")})`;

        // 게임 매출
        sheet.getCell(`${c.cell}4`).value = {
          formula: pointFormula,
        };
        sheet.getCell(`${c.cell}4`).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFF00" },
        };
        // 비율
        sheet.getCell(`${c.cell}6`).value = {
          formula: `=${c.cell}4/($S$4)`,
        };
        return;
      }

      // 게임 매출
      sheet.getCell(`${c.cell}4`).value = {
        formula: `=SUM(${blockRow4.map((r) => `${c.formula}${r}`).join(",")})`,
      };
      sheet.getCell(`${c.cell}4`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" },
      };
      // 각 결제방식 비율
      sheet.getCell(`${c.cell}6`).value = {
        formula: `=${c.cell}4/($S$4)`,
      };
    });

    // 합계 셀
    sheet.getCell("R4").value = "전체 >";
    sheet.getCell("S3").value = "합계";
    sheet.getCell("S4").value = {
      formula: "=SUM(M4:Q5)",
    };

    sheet.getCell("R5").value = "게임매출(현금/계좌/카드)";
    sheet.getCell("S5").value = {
      formula: "=SUM(M4:O4)",
    };

    sheet.getCell("R6").value = "전체매출(포인트/무료 포함)";
    sheet.getCell("S6").value = {
      formula: "=SUM(M4:P4)",
    };

    // 출금내역 =========================================================================
    sheet.getCell(`L8`).value = "출금내역";
    sheet.getCell(`L9`).value = "인건비 ";
    sheet.getCell(`M9`).value = "딜러";
    sheet.getCell(`N9`).value = "카운터";
    sheet.getCell(`O9`).value = "플로어";
    sheet.getCell(`P9`).value = " ";
    sheet.getCell(`Q9`).value = "합계";
    sheet.getCell(`R9`).value = "래빗 시상내역";
    sheet.getCell(`L10`).value = "현금";
    sheet.getCell(`L11`).value = "계좌";

    // 인건비 ===========================================================================
    const laborCell = [
      { cell: "M10", type: ExpenseSubType.DEALER, method: PaymentMethod.CASH },
      {
        cell: "M11",
        type: ExpenseSubType.DEALER,
        method: PaymentMethod.BANK_TRANSFER,
      },
      { cell: "N10", type: ExpenseSubType.COUNTER, method: PaymentMethod.CASH },
      {
        cell: "N11",
        type: ExpenseSubType.COUNTER,
        method: PaymentMethod.BANK_TRANSFER,
      },
      { cell: "O10", type: ExpenseSubType.FLOOR, method: PaymentMethod.CASH },
      { cell: "O11", type: ExpenseSubType.FLOOR, method: PaymentMethod.CARD },
      { cell: "P10", type: null, method: PaymentMethod.CASH },
    ];
    laborCell.forEach((c) => {
      const labor = laborExpense.filter(
        (l) => l.subType === c.type && l.paymentMethod === c.method
      );
      const amount = labor.reduce((sum, curr) => (sum += curr.amount), 0);
      sheet.getCell(`${c.cell}`).value = amount;
    });

    // 인건비 합계
    sheet.getCell("Q10").value = {
      formula: `=SUM(M10,N10,O10,P10)`,
    };
    sheet.getCell("Q11").value = {
      formula: `=SUM(M11,N11,O11)`,
    };
    sheet.getCell("Q12").value = {
      formula: `=SUM(Q10:Q11)`,
    };

    // 래빗 시상 내역 =======================================================================
    const blockRow7 = tournamentList.map((_, i) => 3 + i * 10 + 7);
    sheet.getCell(`R10`).value = {
      formula: `=SUM(${blockRow7.map((r) => `B${r}`).join(",")})`,
    };

    // 지출 내역 ===========================================================================
    expense.forEach((e, i) => {
      const num = i + 14;
      let cell = "";
      // 값 없어도 border 추가
      if (e.paymentMethod === PaymentMethod.CASH) {
        cell = `M${num}`;
        [`O${num}`, `N${num}`].forEach(
          (b) =>
            (sheet.getCell(b).border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            })
        );
      } else if (e.paymentMethod === PaymentMethod.BANK_TRANSFER) {
        cell = `N${num}`;
        [`M${num}`, `O${num}`].forEach(
          (b) =>
            (sheet.getCell(b).border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            })
        );
      } else {
        cell = `O${num}`;
        [`M${num}`, `N${num}`].forEach(
          (b) =>
            (sheet.getCell(b).border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            })
        );
      }

      sheet.getCell(`L${num}`).value = e.description;
      sheet.getCell(cell).value = e.amount;
      sheet.getCell(`L${num}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFCC" },
      };
    });

    // 지출내역 합계
    sheet.getCell(`L${lastCell + 1}`).value = "지출합계";
    sheet.getCell(`L${lastCell + 1}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFFCC" },
    };
    sheet.getCell(`M${lastCell + 1}`).value = expense.length
      ? {
          formula: `=SUM(M14:M${lastCell})`,
        }
      : 0;
    sheet.getCell(`N${lastCell + 1}`).value = expense.length
      ? {
          formula: `=SUM(N14:N${lastCell})`,
        }
      : 0;
    sheet.getCell(`O${lastCell + 1}`).value = expense.length
      ? {
          formula: `=SUM(O14:O${lastCell})`,
        }
      : 0;
    sheet.getCell(`P${lastCell}`).value = "지출총합계";
    sheet.getCell(`P${lastCell + 1}`).value = expense.length
      ? {
          formula: `=SUM(M${lastCell + 1}:O${lastCell + 1})`,
        }
      : 0;

    // 디파짓 수익 ===========================================================================
    const depositCellNum = lastCell + 5;
    const depositCell = [
      { cell: `L${depositCellNum}`, method: PaymentMethod.CASH },
      { cell: `M${depositCellNum}`, method: PaymentMethod.BANK_TRANSFER },
      { cell: `N${depositCellNum}`, method: PaymentMethod.CARD },
    ];

    sheet.getCell(`L${depositCellNum - 2}`).value = "디파짓 수익";
    depositCell.forEach((c) => {
      const value = revenue.depositRevenue.find(
        (d) => c.method === d.paymentMethod
      );
      sheet.getCell(c.cell).value = value?.amount;
    });
    // 합계
    sheet.getCell(`O${depositCellNum}`).value = {
      formula: `=SUM(L${depositCellNum}:N${depositCellNum})`,
    };

    // 전일 잔액합 ===========================================================================
    const yesterdayCellNum = depositCellNum + 4;
    sheet.getCell(`L${yesterdayCellNum - 2}`).value = "전일 잔액합";
    sheet.getCell(`L${yesterdayCellNum}`).value = data?.yesterdayBalance.cash; // 현금
    sheet.getCell(`M${yesterdayCellNum}`).value = data?.yesterdayBalance.bank; // 계좌
    sheet.getCell(`N${yesterdayCellNum}`).value = 0;
    // 합계
    sheet.getCell(`O${yesterdayCellNum}`).value = {
      formula: `=SUM(L${yesterdayCellNum}:N${yesterdayCellNum})`,
    };

    // 순수익 ===========================================================================
    const lossCellNum = yesterdayCellNum + 4;
    sheet.getCell(`L${lossCellNum - 2}`).value = "순수익";

    const lossCell = [
      {
        cell: `L${lossCellNum}`,
        method: PaymentMethod.CASH,
        formula: `=SUM(M4:M5)-M${lastCell + 1}+L${depositCellNum}`,
      },
      {
        cell: `M${lossCellNum}`,
        method: PaymentMethod.BANK_TRANSFER,
        formula: `=SUM(N4:N5)-N${lastCell + 1}+M${depositCellNum}`,
      },
      {
        cell: `N${lossCellNum}`,
        method: PaymentMethod.CARD,
        formula: `=SUM(O4:O5)-O${lastCell + 1}+N${depositCellNum}`,
      },
    ];

    lossCell.forEach((c) => {
      sheet.getCell(c.cell).value = {
        formula: c.formula,
      };
      sheet.getCell(c.cell).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" },
      };
    });

    // 합계
    sheet.getCell(`O${lossCellNum}`).value = {
      formula: `=SUM(L${lossCellNum}:N${lossCellNum})`,
    };
    sheet.getCell(`O${lossCellNum}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF00" },
    };

    // 장부 잔액합 ===========================================================================
    const balanceCellNum = lossCellNum + 4;
    sheet.getCell(`L${balanceCellNum - 2}`).value = "장부 잔액합";

    const balanceCell = [
      {
        cell: `L${balanceCellNum}`,
        method: PaymentMethod.CASH,
        formula: `=L${yesterdayCellNum}+L${lossCellNum}`,
      },
      {
        cell: `M${balanceCellNum}`,
        method: PaymentMethod.BANK_TRANSFER,
        formula: `=M${yesterdayCellNum}+M${lossCellNum}`,
      },
      {
        cell: `N${balanceCellNum}`,
        method: PaymentMethod.CARD,
        formula: `=N${yesterdayCellNum}+N${lossCellNum}`,
      },
    ];

    balanceCell.forEach((c) => {
      sheet.getCell(c.cell).value = {
        formula: c.formula,
      };
      sheet.getCell(c.cell).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" },
      };
    });

    // 합계
    sheet.getCell(`O${balanceCellNum}`).value = {
      formula: `=SUM(L${balanceCellNum}:N${balanceCellNum})`,
    };
    sheet.getCell(`O${balanceCellNum}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF00" },
    };

    // 정산 잔액합 ===========================================================================
    const settlementCellNum = balanceCellNum + 4;
    const realBalanceCellNum = settlementCellNum + 4;
    sheet.getCell(`L${settlementCellNum - 2}`).value = "정산 잔액합";

    const settlementCell = [
      {
        cell: `L${settlementCellNum}`,
        method: PaymentMethod.CASH,
        formula: `=L${realBalanceCellNum}`,
      },
      {
        cell: `M${settlementCellNum}`,
        method: PaymentMethod.BANK_TRANSFER,
        formula: `=M${realBalanceCellNum}-P${settlementCellNum}+Q${settlementCellNum}`,
      },
      {
        cell: `N${settlementCellNum}`,
        method: PaymentMethod.CARD,
        formula: `=N${balanceCellNum}`,
      },
    ];

    const settlementCardCell = [
      {
        cell: `P`,
        name: "당일카드입금",
        value: data.balance.cardIncome,
      },
      {
        cell: `Q`,
        name: "당일카드출금",
        value: data.balance.cardWithdrawal,
      },
    ];

    settlementCell.forEach((c) => {
      sheet.getCell(c.cell).value = {
        formula: c.formula,
      };
      sheet.getCell(c.cell).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" },
      };
    });

    // 카드 입금, 출금
    settlementCardCell.forEach((c) => {
      sheet.getCell(c.cell + (settlementCellNum - 1)).value = c.name;
      sheet.getCell(c.cell + settlementCellNum).value = c.value;
    });

    // 합계
    sheet.getCell(`O${settlementCellNum}`).value = {
      formula: `=SUM(L${settlementCellNum}:N${settlementCellNum})`,
    };
    sheet.getCell(`O${settlementCellNum}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF00" },
    };

    // 실 잔고 ================================================================================
    const currentCellNum = settlementCellNum + 4;
    sheet.getCell(`M${settlementCellNum - 2}`).value = {
      formula: `=IF(O${balanceCellNum}=O${settlementCellNum},"정산 합격","정산 불합격")`,
    };
    sheet.getCell(`L${currentCellNum - 2}`).value = "실 잔고";
    sheet.getCell(`L${currentCellNum}`).value = data?.balance.cash; // 현금
    sheet.getCell(`M${currentCellNum}`).value = data?.balance.bank; // 계좌
    sheet.getCell(`N${currentCellNum - 1}`).value = "래빗";
    sheet.getCell(`N${currentCellNum}`).value = seedInfo?.currentSeed; // 래빗
    // 합계
    sheet.getCell(`O${currentCellNum}`).value = {
      formula: `=SUM(L${currentCellNum}:M${currentCellNum})`,
    };
    sheet.getCell(`O${currentCellNum}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF00" },
    };
    sheet.getCell(`O${currentCellNum + 1}`).value = {
      formula: `=O${currentCellNum}-O${settlementCellNum}`,
    };
    sheet.getCell(`O${currentCellNum + 1}`).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFF00" },
    };

    // 래빗 =================================================================================
    const seedCellNum = yesterdayCellNum - 1;
    sheet.getCell(`P${seedCellNum}`).value = "전일 래빗";
    sheet.getCell(`P${seedCellNum + 1}`).value = seedInfo?.yesterdaySeed;
    sheet.getCell(`P${seedCellNum + 2}`).value = "매출 래빗";
    sheet.getCell(`P${seedCellNum + 3}`).value = seedInfo.revenueSeed;
    sheet.getCell(`P${seedCellNum + 4}`).value = "지출 래빗";
    sheet.getCell(`P${seedCellNum + 5}`).value = seedInfo?.expenseSeed;
    sheet.getCell(`P${seedCellNum + 6}`).value = "본사 래빗";
    sheet.getCell(`P${seedCellNum + 7}`).value = seedInfo?.royaltySeed;
    sheet.getCell(`P${seedCellNum + 8}`).value = "정산 잔고 래빗";
    sheet.getCell(`P${seedCellNum + 9}`).value = {
      formula: `=P${seedCellNum + 1}+P${seedCellNum + 3}-P${seedCellNum + 5}+P${
        seedCellNum + 7
      }`,
    };
    for (let i = 1; i <= 9; i += 2) {
      sheet.getCell(`P${seedCellNum + i}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF00" },
      };
    }
    sheet.getCell(`P${seedCellNum + 10}`).value = {
      formula: `=IF(P${
        seedCellNum + 9
      }=N${currentCellNum},"래빗정산합격","래빗정산불합격")`,
    };

    // 셀 스타일 설정 =========================================================================
    sheet.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell((cell) => {
        // 값 있는 셀에만 border 적용
        if (cell.value !== null && cell.value !== undefined) {
          if (row.number === 1) return;

          cell.font = { bold: true };

          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };

          // 입출금 내역 래빗 배경색 추가
          if (cell.value.toString().includes("래빗")) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "F79646" },
            };
          }

          // 배경색 추가
          if (backgroundBlue.has(String(cell.value))) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "D4DEE8" },
            };
          }

          // 배경색 추가
          if (backgroundPink.has(String(cell.value))) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "E6B8B7" },
            };
          }
        }
      });
    });

    // 래빗 배경색 다시 입히기
    sheet.getCell("Q3").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "F79646" },
    };

    // 지출 내역 배경색 입히기
    expense.forEach((_, i) => {
      const num = i + 14;
      sheet.getCell(`L${num}`).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFCC" },
      };
    });

    // 공통 셀 스타일 설정 =====================================================================
    for (let col = 1; col <= 19; col++) {
      const column = sheet.getColumn(col);

      // 열 너비 설정
      if (col === 18) column.width = 28; // R
      else if (col === 10 || col === 11) column.width = 3; // J, K
      else column.width = 15.5;

      // 열 전체 가운데 정렬
      column.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
        };

        cell.font = {
          ...cell.font,
          name: "맑은 고딕 Bold",
        };

        // 숫자 포맷
        if (typeof cell.value === "number" || typeof cell.value === "object") {
          cell.numFmt = "₩ #,##0";
        }
      });
    }

    // 비율 셀 포맷
    const rateCells = ["M6", "N6", "O6", "P6", "Q6"];
    rateCells.forEach((cellRef) => {
      const cell = sheet.getCell(cellRef);

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "BFBFBF" },
      };
      // 퍼센트 포맷 적용
      cell.numFmt = "0.00%";
    });

    // 굵은 테두리
    sheet.getCell(`P${seedCellNum + 10}`).border = {
      top: { style: "medium" },
      left: { style: "medium" },
      bottom: { style: "medium" },
      right: { style: "medium" },
    };
    sheet.getCell(`M${settlementCellNum - 2}`).border = {
      top: { style: "medium" },
      left: { style: "medium" },
      bottom: { style: "medium" },
      right: { style: "medium" },
    };

    // 첫 번째 행 높이도 그대로 유지
    sheet.getRow(1).height = 28;

    excelDownload(workbook, `${date}_${pub?.pubName}_정산표`);
  };

  const excelDownload = async (workbook: Workbook, fileName: string) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName + ".xlsx";
    anchor.click();
    window.URL.revokeObjectURL(url);
  };

  return {
    createDailyFile,
  };
};
